/* eslint-disable no-unused-vars */
/* eslint-disable no-prototype-builtins */
import { ReactNode, Children } from 'react'

interface EachProps<T> {
  of?: T[]
  render: (item: T, index: number) => ReactNode
}

export const get = (obj: object, path: string, defaultValue?: never) => {
  const travel = (regexp: RegExp) =>
    String.prototype.split
      .call(path, regexp)
      .filter(Boolean)
      .reduce((res, key) => (res !== null && res !== undefined ? res[key] : res), obj)
  const result = travel(/[,[\]]+?/) || travel(/[,[\].]+?/)
  return result === undefined || result === obj ? defaultValue : result
}

export const isUndefined = (value: unknown) => value === undefined

export const isNull = (value: unknown) => value === null

export const isNil = (value: unknown) => value === null || value === undefined

export const isEmpty = (obj) => {
  return [Object, Array].includes((obj || {}).constructor) && !Object.entries(obj || {}).length
}

export const range = (length: number) => {
  return Array.from({ length }, (_, i) => i)
}

export const rangeFrom = (start: number, stop: number, step: number) => {
  return Array.from({ length: (stop - start) / step + 1 }, (_, i) => start + i * step)
}

export const sortBy = (value: Array<unknown>, key: string, type?: 'asc' | 'desc') => {
  if (type === 'desc') {
    return value.concat().sort((a: object, b: object) => {
      if (a[key] > b[key]) {
        return -1
      } else if (b[key] > a[key]) {
        return 1
      } else {
        return 0
      }
    })
  } else {
    return value.concat().sort((a: object, b: object) => {
      if (a[key] > b[key]) {
        return 1
      } else if (b[key] > a[key]) {
        return -1
      } else {
        return 0
      }
    })
  }
}

export const cloneDeep = (obj: object) => {
  return JSON.parse(JSON.stringify(obj))
}

export const pick = (object: object, keys: Array<string>) => {
  return keys.reduce((obj, key) => {
    if (object && object.hasOwnProperty(key)) {
      obj[key] = object[key]
    }
    return obj
  }, {})
}

export const pickBy = (value: object, callback: (value: string) => boolean) => {
  const obj = {}
  for (const key in value) {
    if (callback(value[key])) {
      obj[key] = value[key]
    }
  }
  return obj
}

export const omit = (obj: object, keys: Array<string>) => {
  const object = {}
  Object.keys(obj).forEach((key) => {
    if (!keys.includes(key)) {
      object[key] = obj[key]
    }
  })
  return object
}

export const omitBy = (obj: object, callback: (value: string) => boolean) => {
  const object = {}
  Object.keys(obj).forEach((key) => {
    if (!callback(obj[key])) {
      object[key] = obj[key]
    }
  })
  return object
}

export const groupBy = <T>(arr: T[], fn: (item: T) => any) =>
  arr.reduce<Record<string, T[]>>((prev, curr) => {
    const groupKey = fn(curr)
    const group = prev[groupKey] || []
    group.push(curr)
    return { ...prev, [groupKey]: group }
  }, {})

export const union = (...arrays: Array<unknown>) => {
  return arrays.flat().filter((value, index, self) => self.indexOf(value) === index)
}

export const assign = (init: object, ...value: object[]) => {
  return Object.assign(init, ...value)
}

export const mapValues = (obj: object, iteratee: unknown) => {
  const newObj = {}
  if (typeof iteratee === 'function') {
    Object.keys(obj).forEach((val) => {
      Object.assign(newObj, { [val]: iteratee(obj[val]) })
    })
  } else {
    Object.keys(obj).forEach((val) => {
      Object.assign(newObj, { [val]: obj[val][iteratee] })
    })
  }
  return newObj
}

export const maskName = (name: string): string => {
  // Split the name into parts
  const parts: string[] = name.split(' ')

  // Mask the first part (first name)
  if (parts.length > 0) {
    parts[0] = maskString(parts[0])
  }

  // Mask the last part (last name)
  if (parts.length > 1) {
    parts[parts.length - 1] = maskString(parts[parts.length - 1])
  }

  // Join the parts back together
  return parts.join(' ')
}

export const maskString = (str: string): string => {
  // Mask all characters except the first one
  return str[0] + '*'.repeat(str.length - 1)
}

export const maskEmail = (email: string): string => {
  const atIndex = email.indexOf('@')
  if (atIndex <= 1) {
    return email
  }

  const [localPart, domain] = email.split('@')
  const maskedLocalPart =
    localPart.charAt(0) + '*'.repeat(localPart.length - 2) + localPart.charAt(localPart.length - 1)

  return maskedLocalPart + '@' + domain
}

export const Each = <T>({ of, render }: EachProps<T>): ReactNode[] =>
  Children.toArray(of?.map((item, index) => render(item, index)))

export const maskData = (str: string, notMarked = 1, isSuffix?: boolean): string => {
  // Mask character based on not marked count
  const notMarkedWord =
    notMarked < 2 ? str[notMarked] : isSuffix ? str.substr(-notMarked) : str.substring(0, notMarked)

  if (isSuffix) return '*'.repeat(str.length - notMarkedWord.length) + notMarkedWord

  return notMarkedWord + '*'.repeat(str.length - notMarkedWord.length)
}

// eslint-disable-next-line @typescript-eslint/no-inferrable-types
export const maskWords = (name: string, isSuffix: boolean = false): string => {
  const words = name.split(" ");
  
  const maskedWords = words.map(word => maskData(word, 3, isSuffix));

  return maskedWords.join(" ");
};
